import AudioIcon from '@assets/icons/audio.svg';
import ImageIcon from '@assets/icons/file-image-small.svg';
import PdfIcon from '@assets/icons/file-pdf.svg';
import SmsIcon from '@assets/icons/sms.svg';
import VoicemailIcon from '@assets/icons/voicemail.svg';
import {
  AttachmentMimeType,
  MimeTypeByExtension,
  OrderMetadata,
} from '@/screens/order-inbounds/types';
import { createStyles } from '@/contexts/theme-context';
import { FC, ReactNode, useMemo } from 'react';
import { InboundAttachment } from '@/components/attachment-list/attachment-list-types';
import { Pressable, Text } from 'react-native';
import type { OnAttachmentListItemPressCallback } from '@/screens/order-inbounds/order-inbounds-detail-tabs/types';

interface Props {
  attachment: InboundAttachment;
  onPress: OnAttachmentListItemPressCallback;
  order: OrderMetadata;
}

export const OrderInboundsDetailEmlAttachmentListItem: FC<Props> = ({
  attachment,
  onPress,
  order,
}) => {
  const [styles, theme] = useStyles();

  const itemIcon: ReactNode = useMemo(() => {
    const iconColor = theme.sk.color.icon.secondary;
    switch (order.source) {
      case 'VOICEMAIL':
        return <VoicemailIcon fill={iconColor} />;
      case 'SMS':
        return <SmsIcon fill={iconColor} />;
      default:
        break;
    }

    // determine the MIME type to use based on file extension if available
    let resolvedMimeType: AttachmentMimeType = attachment.mimeType;

    if (attachment.originalFilename) {
      // Extract extension from filename (part after last dot)
      const parts = attachment.originalFilename.split('.');
      if (parts.length > 1) {
        const extension = parts.pop()?.toLowerCase();

        if (extension && extension in MimeTypeByExtension) {
          const mappedType =
            MimeTypeByExtension[extension as keyof typeof MimeTypeByExtension];
          resolvedMimeType = mappedType as unknown as AttachmentMimeType;
        }
      }
    }

    switch (resolvedMimeType) {
      case AttachmentMimeType.GIF:
      case AttachmentMimeType.JPG:
      case AttachmentMimeType.PNG:
      case AttachmentMimeType.HEIC:
      case AttachmentMimeType.WEBP:
        return <ImageIcon fill={iconColor} height={16} width={16} />;
      case AttachmentMimeType.PDF:
        return <PdfIcon height={16} stroke={iconColor} width={16} />;
      case AttachmentMimeType.AAC:
      case AttachmentMimeType.AMR:
      case AttachmentMimeType.M4A:
      case AttachmentMimeType.MP3:
      case AttachmentMimeType.MP4_AUDIO:
      case AttachmentMimeType.OGG:
      case AttachmentMimeType.OPUS:
      case AttachmentMimeType.THREE_GPP:
      case AttachmentMimeType.WAV:
      case AttachmentMimeType.WEBM_AUDIO:
      case AttachmentMimeType.X_WAV:
        return <AudioIcon height={16} stroke={iconColor} width={16} />;
      default:
        break;
    }

    return null;
  }, [order, attachment, theme.sk.color.icon.secondary]);

  return (
    <Pressable onPress={() => onPress(attachment)} style={styles.container}>
      {itemIcon}

      <Text style={styles.itemText}>
        {attachment.originalFilename ?? attachment.url}
      </Text>
    </Pressable>
  );
};

const useStyles = createStyles((theme) => ({
  container: {
    alignItems: 'center',
    backgroundColor: theme.sk.color.bg.primary.default,
    borderColor: theme.sk.color.border.primary,
    borderRadius: 8,
    borderWidth: 1,
    cursor: 'pointer',
    flexDirection: 'row',
    gap: 8,
    justifyContent: 'center',
    padding: 16,
  },
  itemText: {
    color: theme.sk.color.text.secondary,
    fontSize: 14,
    fontWeight: 400,
  },
}));
